import DomainDetails from "types/domainDetails";

export const toTitleCase = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2") // split camelCase
    .split(/[\s_./-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

export const hasText = (value: string | null | undefined): boolean =>
  typeof value === "string" && value.trim().length > 0;

export const toThemePath = (value: string): string =>
  `/domains/${value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")}`;

export const toList = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

// ── Deterministic phrase variation ─────────────────────────────────────────

export const domainHash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

export const hashPick = <T>(arr: T[], hash: number, salt: number): T =>
  arr[(hash + salt) % arr.length];

export const CATEGORY_CONNECTORS = [
  "-focused",
  "-oriented",
  "-driven",
  "-related",
];

export const THEME_CONNECTORS = [
  "with clear associations to",
  "with strong ties to",
  "drawing on themes of",
  "with connections to",
];

export const SCORE_OPENERS: Array<(s: number) => string> = [
  (s) => `With a brand score of ${s}/100,`,
  (s) => `Rated ${s}/100 for branding,`,
  (s) => `Scoring ${s}/100 on the brand scale,`,
  (s) => `At a brand score of ${s}/100,`,
];

export const HIGH_PRONOUNCEABILITY_PHRASES = [
  "Its clean, memorable sound makes it easy to say and share — a strong foundation for a recognisable brand.",
  "The name rolls off the tongue naturally, making it easy to recommend and remember.",
  "Its smooth phonetics make it instantly speakable and shareable across any audience.",
  "Easy to pronounce at first glance, it reduces friction when people hear or read the name.",
  "Its natural flow makes it stand out as a brand that people can confidently say and repeat.",
];

export const MEDIUM_PRONOUNCEABILITY_PHRASES = [
  "Its balanced readability helps it feel modern while remaining brandable.",
  "Its medium pronounceability gives it a startup-style feel while remaining readable enough for a brand name.",
  "The name takes a moment to land, which can make it feel distinctive and memorable once it does.",
  "Its slight complexity adds character without becoming hard to recall or share.",
  "It sits in that sweet spot between effortless and intriguing — approachable, yet not generic.",
  "A name that rewards a second glance — memorable once heard, with enough edge to stand out.",
];

export const LOW_PRONOUNCEABILITY_PHRASES = [
  "Its abstract style suits projects that value distinctiveness over immediate readability.",
  "The unconventional sound makes it harder to say on first sight, but can feel uniquely ownable.",
  "Its challenging phonetics lean into the abstract, which works well for technical or avant-garde brands.",
  "Not immediately readable, but that opacity can be an asset for brands that want to feel cryptic or niche.",
];

export const STRONG_SCORE_COMPLETIONS = [
  "it has strong commercial branding potential.",
  "it stands out as a highly brandable name.",
  "it carries real weight as a commercial brand.",
  "it makes a strong candidate for a standout brand name.",
];

export const SOLID_SCORE_COMPLETIONS = [
  "it shows solid promise as a commercial brand name.",
  "it has good potential for brand recognition.",
  "it's a capable name for building brand identity.",
  "it offers a reliable foundation for a recognisable brand.",
];

export const NICHE_SCORE_COMPLETIONS = [
  "it's best suited to niche or experimental projects.",
  "it works well in specialised or technical contexts.",
  "it suits projects where distinctiveness matters more than mass appeal.",
  "it fits well within niche audiences or focused communities.",
];

export const STRONG_SCORE_PHRASES = [
  "with strong branding potential",
  "with strong brand appeal",
  "with a high brand score",
  "showing strong brand value",
];

export const SOLID_SCORE_PHRASES = [
  "with solid branding potential",
  "with good brand appeal",
  "with a solid brand score",
  "showing good brand value",
];

export const USE_CASES: Record<string, string> = {
  "artificial intelligence": "AI tools",
  "machine learning": "ML platforms",
  ai: "AI tools",
  analytics: "analytics tools",
  automation: "automation software",
  blockchain: "blockchain tools",
  cloud: "cloud services",
  communication: "communication tools",
  crypto: "crypto platforms",
  data: "data tools",
  design: "design tools",
  ecommerce: "online stores",
  education: "edtech tools",
  feedback: "feedback platforms",
  finance: "fintech products",
  fitness: "fitness platforms",
  food: "food apps",
  gaming: "gaming platforms",
  health: "health platforms",
  innovation: "experimental products",
  lab: "research tools",
  laboratories: "research platforms",
  marketing: "marketing tools",
  media: "media platforms",
  mobile: "mobile apps",
  productivity: "productivity tools",
  research: "research platforms",
  security: "security software",
  social: "social platforms",
  software: "software products",
  technology: "tech products",
  travel: "travel platforms",
};

export const STRUCTURE_TYPE_DESCRIPTIONS: Record<string, string> = {
  exact_match:
    "Exact-match domains use a real dictionary word as-is, offering immediate clarity and strong keyword relevance.",
  compound:
    "Compound domains join two or more real words together, combining familiarity with a distinctive brand identity.",
  keyword:
    "Keyword domains use a descriptive word or phrase that signals the domain's topic directly to visitors.",
  invented:
    "Invented domains are coined brand names with no direct word split, designed to be unique and ownable.",
  acronym:
    "Acronym domains compress a longer name into an abbreviated string — quick to type and easy to recall.",
  numeric:
    "Numeric domains incorporate significant numbers, giving them a distinct and memorable character.",
};

export const buildDomainSummary = (
  domain: Pick<
    DomainDetails,
    | "domain"
    | "primaryCategory"
    | "structureType"
    | "semanticTokens"
    | "pronounceability"
    | "brandScore"
  >,
  relatedThemes: string[],
): string[] => {
  const h = domainHash(domain.domain);

  const category = hasText(domain.primaryCategory)
    ? toTitleCase(domain.primaryCategory as string).toLowerCase()
    : null;
  const structure = hasText(domain.structureType)
    ? toTitleCase(domain.structureType as string).toLowerCase()
    : null;

  const catConnector = hashPick(CATEGORY_CONNECTORS, h, 0);
  const themeConnector = hashPick(THEME_CONNECTORS, h, 1);

  const intro = (() => {
    const themeText =
      relatedThemes.length > 0
        ? ` ${themeConnector} ${toList(relatedThemes.map((t) => t.toLowerCase()))}`
        : "";

    if (structure && category) {
      const article = /^[aeiou]/i.test(structure) ? "an" : "a";
      return `${domain.domain} is ${article} ${structure}, ${category}${catConnector} domain${themeText}.`;
    }

    if (structure) {
      const article = /^[aeiou]/i.test(structure) ? "an" : "a";
      return `${domain.domain} is ${article} ${structure} domain${themeText}.`;
    }

    if (category) {
      return `${domain.domain} is a ${category}${catConnector} domain${themeText}.`;
    }

    if (relatedThemes.length > 0) {
      return `${domain.domain} has ${themeConnector} ${toList(relatedThemes.map((t) => t.toLowerCase()))}.`;
    }

    return null;
  })();

  const p = domain.pronounceability?.toLowerCase();
  const pronounceability =
    p === "high"
      ? hashPick(HIGH_PRONOUNCEABILITY_PHRASES, h, 3)
      : p === "medium"
        ? hashPick(MEDIUM_PRONOUNCEABILITY_PHRASES, h, 4)
        : p === "low"
          ? hashPick(LOW_PRONOUNCEABILITY_PHRASES, h, 5)
          : null;

  const score =
    domain.brandScore !== undefined && domain.brandScore !== null
      ? (() => {
          const s = domain.brandScore;
          const opener = hashPick(SCORE_OPENERS, h, 2)(s);
          const completion =
            s >= 75
              ? hashPick(STRONG_SCORE_COMPLETIONS, h, 6)
              : s >= 60
                ? hashPick(SOLID_SCORE_COMPLETIONS, h, 7)
                : hashPick(NICHE_SCORE_COMPLETIONS, h, 8);
          return `${opener} ${completion}`;
        })()
      : null;

  return [intro, pronounceability, score].filter(
    (sentence): sentence is string => Boolean(sentence),
  );
};

export const getBrandPotentialLabel = (score: number): string => {
  if (score >= 90) return "Excellent brandability";
  if (score >= 75) return "Strong branding potential";
  if (score >= 60) return "Good branding potential";
  if (score >= 40) return "Moderate brandability";
  return "Weak brandability";
};

export const getCategoryDisplayLabel = (
  category: string,
  confidence: number | null | undefined,
): string => {
  const label = toTitleCase(category);
  if (confidence == null || confidence >= 0.75) return label;
  if (confidence >= 0.45) return `Likely ${label.toLowerCase()}`;
  return `${label}-related`;
};

// ── JSON-LD structured data ─────────────────────────────────────────────────

export const buildDomainJsonLd = (
  domain: string,
  domainDetails: DomainDetails,
  metaTitle: string,
  metaDescription: string,
  siteUrl: string,
): string => {
  const pageUrl = `${siteUrl}/domain/${domain}`;
  const breadcrumbId = `${pageUrl}#breadcrumb`;
  const domainEntityId = `${pageUrl}#domain`;
  const structureTermId = `${pageUrl}#structureType`;

  const graph: object[] = [];

  // BreadcrumbList
  graph.push({
    "@type": "BreadcrumbList",
    "@id": breadcrumbId,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
      { "@type": "ListItem", position: 2, name: domain, item: pageUrl },
    ],
  });

  // WebPage
  graph.push({
    "@type": "WebPage",
    "@id": pageUrl,
    url: pageUrl,
    name: metaTitle,
    description: metaDescription,
    breadcrumb: { "@id": breadcrumbId },
    about: { "@id": domainEntityId },
  });

  // Domain entity — Thing
  const additionalProperty: object[] = [];

  if (domainDetails.brandScore != null) {
    additionalProperty.push({
      "@type": "PropertyValue",
      name: "Brand Score",
      value: domainDetails.brandScore,
    });
  }
  if (hasText(domainDetails.primaryCategory)) {
    additionalProperty.push({
      "@type": "PropertyValue",
      name: "Category",
      value: getCategoryDisplayLabel(
        domainDetails.primaryCategory as string,
        domainDetails.primaryCategoryConfidence,
      ),
    });
  }
  if (hasText(domainDetails.structureType)) {
    additionalProperty.push({
      "@type": "PropertyValue",
      name: "Structure Type",
      value: toTitleCase(domainDetails.structureType as string),
    });
  }
  if (hasText(domainDetails.pronounceability)) {
    additionalProperty.push({
      "@type": "PropertyValue",
      name: "Pronounceability",
      value: toTitleCase(domainDetails.pronounceability as string),
    });
  }

  const keywords = (domainDetails.semanticTokens ?? [])
    .filter((t) => hasText(t))
    .map((t) => t.trim().toLowerCase());

  const domainEntity: Record<string, unknown> = {
    "@type": "Thing",
    "@id": domainEntityId,
    name: domain,
    description: metaDescription,
  };
  if (keywords.length > 0) domainEntity.keywords = keywords.join(", ");
  if (additionalProperty.length > 0)
    domainEntity.additionalProperty = additionalProperty;
  if (domainDetails.registered && hasText(domainDetails.creation))
    domainEntity.dateCreated = domainDetails.creation;

  graph.push(domainEntity);

  // DefinedTerm for structureType (only when we have a description for it)
  if (hasText(domainDetails.structureType)) {
    const key = (domainDetails.structureType as string).trim().toLowerCase();
    const structureDescription = STRUCTURE_TYPE_DESCRIPTIONS[key];
    if (structureDescription) {
      graph.push({
        "@type": "DefinedTerm",
        "@id": structureTermId,
        name: `${toTitleCase(domainDetails.structureType as string)} domain`,
        description: structureDescription,
        inDefinedTermSet: {
          "@type": "DefinedTermSet",
          name: "Domain Structure Types",
        },
      });
    }
  }

  return JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
};
